import { Component, inject, signal, WritableSignal, ChangeDetectionStrategy } from "@angular/core";
import { ButtonComponent } from "../../../../shared/components/button/button.component";
import { FileSelectEvent, FileUpload } from "@openng/optimus-ui/fileupload";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { first } from "rxjs";
import { DynamicDialogRef } from "@openng/optimus-ui/dynamicdialog";
import { createTimeoutSignal } from "../../../../shared/utils/timeout.utils";
import { TimeoutComponent } from "../../../../shared/components/timeout/timeout.component";
import { MessageService } from "@openng/optimus-ui/api";
import { ImportService } from "../../../../services/api/import/import.service";
import { MessageModule } from "@openng/optimus-ui/message";

@Component({
  selector: "app-device-import",
  imports: [ButtonComponent, FileUpload, TranslateModule, TimeoutComponent, MessageModule],
  templateUrl: "./device-import.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: "./device-import.scss",
})
export class DeviceImport {
  public translate = inject(TranslateService);
  public selectedFile!: File | null;
  private importService = inject(ImportService);
  private ref = inject(DynamicDialogRef);
  public loadingImport = signal(false);
  public timeoutImport = createTimeoutSignal(this.loadingImport);
  public failedImport = signal(false);
  private message = inject(MessageService);

  public failedCsvFilename: WritableSignal<string | null> = signal(null);

  /**
   * Downloads the example file for the device import feature.
   * The example file is downloaded as a CSV file with the name specified in the translation.
   */
  public downloadExample() {
    this.importService
      .getDeviceExampleFile()
      .pipe(first())
      .subscribe({
        next: (res) => {
          if (res) {
            const blob = new Blob([res.body!], {
              type: "text/csv;charset=utf-8",
            });
            const link = document.createElement("a");

            link.download = this.translate.instant(`device_management.import.example_file_name`);
            link.href = window.URL.createObjectURL(blob);
            link.style.display = "none";
            document.body.appendChild(link);

            link.click();
            document.body.removeChild(link);
          }
        },
      });
  }

  /**
   * Sets the selected file for the import to the file selected in the file upload.
   * @param event The file select event.
   */
  public onFileSelect(event: FileSelectEvent) {
    const file = event.files[0];
    this.selectedFile = file;
  }
  /**
   * Closes the dialog and returns no value.
   */
  public closeDialog() {
    this.ref.close();
  }

  /**
   * Saves the selected file and type as a new import.
   * Displays a message on success or partial error.
   * If the import is partial, displays a warning .
   * If the import is successful, closes the dialog.
   */
  public save() {
    if (this.selectedFile) {
      this.importService.createDeviceImport(this.selectedFile).subscribe({
        next: (res) => {
          if (res.importFailedCsvFilename) {
            this.message.add({
              key: "global",
              severity: "warn",
              summary: this.translate.instant("device_management.import.messages.partial"),
              life: 5000,
            });
            this.failedCsvFilename.set(res.importFailedCsvFilename);
          } else {
            this.message.add({
              key: "global",
              severity: "success",
              summary: this.translate.instant("device_management.import.messages.success"),
              life: 5000,
            });
            this.failedCsvFilename.set(null);
            this.ref.close();
          }
        },
      });
    }
  }

  /**
   * Downloads the failed csv import file from the server.
   * If a filename for the failed CSV is available, requests the file from the server,
   * creates a downloadable link, and programmatically clicks it to start the download.
   * @param failedCsvFilename The filename of the failed csv file to be retrieved.
   */
  public downloadFailedFile(failedCsvFilename: string | null) {
    if (failedCsvFilename) {
      this.importService
        .getDevicesFailedFile(failedCsvFilename)
        .pipe(first())
        .subscribe({
          next: (res) => {
            if (res) {
              const blob = new Blob([res.body!], {
                type: "text/csv;charset=utf-8",
              });
              const link = document.createElement("a");

              link.href = window.URL.createObjectURL(blob);
              link.style.display = "none";
              document.body.appendChild(link);

              link.click();
              document.body.removeChild(link);
            }
          },
        });
    }
  }
}
